import React, { useState, useRef, useEffect } from 'react';
import { Wifi, WifiOff, Play, Pause, Settings, Menu, X, LogOut, User } from 'lucide-react';
import { MarketProvider, useMarket } from './context/MarketContext';
import { useMarketClock } from './hooks/useMarketClock';
import { useAuth } from './context/AuthContext';
import { PageKey } from './types';

// Components
import { Navigation } from './components/Navigation';
import { Watchlist } from './components/Watchlist';
import OrderBookPanel from './components/OrderBookPanel';
import QuickOrderPanel from './components/QuickOrderPanel';
import RecentTradesList from './components/RecentTradesList';
import SystemStatusPanel from './components/SystemStatus';

// Pages
import DashboardPage from './pages/DashboardPage';
import PositionsPage from './pages/PositionsPage';
import OrdersPage from './pages/OrdersPage';
import AnalysisPage from './pages/AnalysisPage';
import RiskPage from './pages/RiskPage';
import SettingsPage from './pages/SettingsPage';
import LoginPage from './pages/LoginPage';

function AppContent() {
  const { isAuthenticated, loading } = useAuth();
  
  // Show loading state while checking authentication
  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: '#0f172a',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#e2e8f0'
      }}>
        <div>Loading...</div>
      </div>
    );
  }
  
  // Show login page if not authenticated
  if (!isAuthenticated) {
    return <LoginPage />;
  }
  
  // Authenticated - show main app
  return <AuthenticatedApp />;
}

function AuthenticatedApp() {
  const [activePage, setActivePage] = useState<PageKey>('dashboard');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSettingsDropdownOpen, setIsSettingsDropdownOpen] = useState(false);
  const settingsDropdownRef = useRef<HTMLDivElement>(null);
  const market = useMarketClock();
  const { user, logout } = useAuth();

  const {
    selectedSymbol,
    setSelectedSymbol,
    connectionStatus,
    isAutoTrading,
    setIsAutoTrading,
    onRequestData,
    INSTRUMENTS,
    labelForSecId,
    vwapDataBySymbol
  } = useMarket();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (settingsDropdownRef.current && !settingsDropdownRef.current.contains(event.target as Node)) {
        setIsSettingsDropdownOpen(false);
      }
    };

    if (isSettingsDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isSettingsDropdownOpen]);

  const handleLogout = () => {
    logout();
    setIsSettingsDropdownOpen(false);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'connected': return '#10b981';
      case 'connecting': return '#f59e0b';
      case 'disconnected': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const renderPage = () => {
    switch (activePage) {
      case 'dashboard': return <DashboardPage />;
      case 'positions': return <PositionsPage />;
      case 'orders': return <OrdersPage />;
      case 'analysis': return <AnalysisPage />;
      case 'risk': return <RiskPage />;
      case 'settings': return <SettingsPage />;
      default: return <DashboardPage />;
    }
  };

  return (
    <div className="app-container" style={{ 
      backgroundColor: '#0f172a', 
      color: '#e2e8f0', 
      minHeight: '100vh',
      width: '100%',
      minWidth: '100%',
      fontFamily: 'Sora, -apple-system, BlinkMacSystemFont, sans-serif',
      fontSize: '13px'
    }}>
      {/* Header */}
      <div className="app-header" style={{ 
        backgroundColor: '#1e293b', 
        padding: '12px 24px', 
        borderBottom: '1px solid #334155',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        {/* Desktop Layout */}
        <div className="header-left" style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
          <img 
            src="/Shabba-Alternative-Logo-01.png" 
            alt="Shabba Financial" 
            className="header-logo"
            style={{ 
              width: 'auto',
              objectFit: 'contain'
            }} 
          />
          <h1 className="app-title" style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>
            DSE Market Maker Terminal
          </h1>
          <div className="header-status-desktop" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {connectionStatus === 'connected' ? <Wifi size={16} /> : <WifiOff size={16} />}
            <span style={{
              padding: '4px 12px',
              borderRadius: '6px',
              fontSize: '11px',
              fontWeight: '600',
              backgroundColor: getStatusColor(connectionStatus),
              color: 'white'
            }}>
              {connectionStatus.toUpperCase()}
            </span>
          </div>
        </div>

        <div className="header-right" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <select
            value={selectedSymbol}
            onChange={(e) => setSelectedSymbol(e.target.value)}
            style={{
              backgroundColor: '#334155',
              color: '#e2e8f0',
              border: '1px solid #475569',
              borderRadius: '6px',
              padding: '6px 12px',
              fontSize: '12px'
            }}
          >
            {INSTRUMENTS.map(it => (
              <option key={it.secId} value={it.secId}>
                {it.label}
              </option>
            ))}
          </select>
          
          <button
            className="start-trading-button"
            onClick={() => setIsAutoTrading(!isAutoTrading)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 16px',
              backgroundColor: isAutoTrading ? '#ef4444' : '#10b981',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: '500'
            }}
          >
            {isAutoTrading ? <Pause size={14} /> : <Play size={14} />}
            <span className="start-trading-text">{isAutoTrading ? 'Stop Trading' : 'Start Trading'}</span>
          </button>

          <div ref={settingsDropdownRef} style={{ position: 'relative' }}>
            <Settings 
              size={16} 
              onClick={() => setIsSettingsDropdownOpen(!isSettingsDropdownOpen)}
              style={{ cursor: 'pointer', color: '#94a3b8' }} 
            />
            {isSettingsDropdownOpen && (
              <div style={{
                position: 'absolute',
                top: '100%',
                right: 0,
                marginTop: '8px',
                backgroundColor: '#1e293b',
                border: '1px solid #334155',
                borderRadius: '8px',
                padding: '8px 0',
                minWidth: '200px',
                boxShadow: '0 10px 25px rgba(0, 0, 0, 0.3)',
                zIndex: 1000
              }}>
                {/* User Info */}
                <div style={{
                  padding: '12px 16px',
                  borderBottom: '1px solid #334155',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  <div style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    backgroundColor: '#10b981',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontWeight: '600',
                    fontSize: '14px'
                  }}>
                    {user?.name?.charAt(0).toUpperCase() || 'U'}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontSize: '13px',
                      fontWeight: '600',
                      color: '#e2e8f0',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {user?.name || 'User'}
                    </div>
                    <div style={{
                      fontSize: '11px',
                      color: '#94a3b8',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {user?.email || ''}
                    </div>
                  </div>
                </div>
                
                {/* Settings Option */}
                <button
                  onClick={() => {
                    setActivePage('settings');
                    setIsSettingsDropdownOpen(false);
                  }}
                  style={{
                    width: '100%',
                    padding: '10px 16px',
                    backgroundColor: 'transparent',
                    border: 'none',
                    color: '#e2e8f0',
                    fontSize: '13px',
                    textAlign: 'left',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#334155'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  <Settings size={14} />
                  <span>Settings</span>
                </button>

                {/* Logout Button */}
                <button
                  onClick={handleLogout}
                  style={{
                    width: '100%',
                    padding: '10px 16px',
                    backgroundColor: 'transparent',
                    border: 'none',
                    borderTop: '1px solid #334155',
                    color: '#ef4444',
                    fontSize: '13px',
                    textAlign: 'left',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    transition: 'background-color 0.2s',
                    marginTop: '4px'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#7f1d1d'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  <LogOut size={14} />
                  <span>Logout</span>
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Mobile Layout - Single Row */}
        <button
          className="mobile-menu-button"
          onClick={() => setIsSidebarOpen(!isSidebarOpen)}
          style={{
            display: 'none',
            background: 'transparent',
            border: 'none',
            color: '#e2e8f0',
            cursor: 'pointer',
            padding: '4px',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}
          aria-label="Toggle menu"
        >
          {isSidebarOpen ? <X size={24} /> : <Menu size={24} />}
        </button>

        <img 
          src="/Shabba-Alternative-Logo-01.png" 
          alt="Shabba Financial" 
          className="header-logo-mobile"
          style={{ 
            width: 'auto',
            objectFit: 'contain',
            display: 'none'
          }} 
        />

        <div className="header-wifi-mobile" style={{ display: 'none', alignItems: 'center' }}>
          {connectionStatus === 'connected' ? <Wifi size={18} color={getStatusColor(connectionStatus)} /> : <WifiOff size={18} color={getStatusColor(connectionStatus)} />}
        </div>

        <select
          className="header-select-mobile"
          value={selectedSymbol}
          onChange={(e) => setSelectedSymbol(e.target.value)}
          style={{
            display: 'none',
            backgroundColor: '#334155',
            color: '#e2e8f0',
            border: '1px solid #475569',
            borderRadius: '6px'
          }}
        >
          {INSTRUMENTS.map(it => (
            <option key={it.secId} value={it.secId}>
              {it.label}
            </option>
          ))}
        </select>

        <button
          className="start-trading-button-mobile"
          onClick={() => setIsAutoTrading(!isAutoTrading)}
          style={{
            display: 'none',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '3px 5px',
            backgroundColor: isAutoTrading ? '#ef4444' : '#10b981',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer'
          }}
        >
          {isAutoTrading ? <Pause size={12} /> : <Play size={12} />}
        </button>

        <div ref={settingsDropdownRef} style={{ position: 'relative', display: 'none' }} className="header-settings-mobile">
          <Settings 
            size={12} 
            onClick={() => setIsSettingsDropdownOpen(!isSettingsDropdownOpen)}
            style={{ 
              cursor: 'pointer', 
              color: '#94a3b8',
              flexShrink: 0
            }} 
          />
          {isSettingsDropdownOpen && (
            <div style={{
              position: 'absolute',
              top: '100%',
              right: 0,
              marginTop: '8px',
              backgroundColor: '#1e293b',
              border: '1px solid #334155',
              borderRadius: '8px',
              padding: '8px 0',
              minWidth: '180px',
              boxShadow: '0 10px 25px rgba(0, 0, 0, 0.3)',
              zIndex: 1000
            }}>
              <div style={{
                padding: '10px 12px',
                borderBottom: '1px solid #334155',
                fontSize: '12px',
                color: '#e2e8f0',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                {user?.name || 'User'}
              </div>
              <button
                onClick={handleLogout}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  backgroundColor: 'transparent',
                  border: 'none',
                  color: '#ef4444',
                  fontSize: '12px',
                  textAlign: 'left',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#7f1d1d'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                <LogOut size={12} />
                <span>Logout</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Mobile Sidebar Overlay */}
      {isSidebarOpen && (
        <div 
          className="sidebar-overlay"
          onClick={() => setIsSidebarOpen(false)}
          style={{
            display: 'none',
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 998
          }}
        />
      )}

      {/* Main Content */}
      <div className="main-content-grid" style={{ display: 'grid', gridTemplateColumns: '250px 1fr 400px', gap: '12px', padding: '12px' }}>
        
        {/* Left Sidebar */}
        <div 
          className={`left-sidebar ${isSidebarOpen ? 'sidebar-open' : ''}`}
          style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}
        >
          {/* Close button for mobile */}
          <button
            className="sidebar-close-button"
            onClick={() => setIsSidebarOpen(false)}
            style={{
              display: 'none',
              alignSelf: 'flex-end',
              background: 'transparent',
              border: 'none',
              color: '#e2e8f0',
              cursor: 'pointer',
              padding: '8px',
              marginBottom: '-8px'
            }}
            aria-label="Close sidebar"
          >
            <X size={20} />
          </button>
          <Navigation 
            activePage={activePage} 
            setActivePage={(page) => {
              setActivePage(page);
              setIsSidebarOpen(false); // Close sidebar when navigating on mobile
            }} 
          />
          <Watchlist 
            selectedSymbol={selectedSymbol}
            setSelectedSymbol={setSelectedSymbol}
            vwapDataBySymbol={vwapDataBySymbol}
          />
          
          {/* Account Summary */}
          <div style={{ backgroundColor: '#1e293b', padding: '16px', borderRadius: '8px', border: '1px solid #334155' }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: '14px', fontWeight: '600', color: '#94a3b8' }}>ACCOUNT</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ fontSize: '12px', color: '#94a3b8' }}>Buying Power</span>
                <span style={{ fontSize: '12px', fontWeight: '600', color: '#10b981' }}>2.5M</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ fontSize: '12px', color: '#94a3b8' }}>Portfolio Value</span>
                <span style={{ fontSize: '12px', fontWeight: '600' }}>5.75M</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ fontSize: '12px', color: '#94a3b8' }}>Day P&L</span>
                <span style={{ fontSize: '12px', fontWeight: '600', color: '#ef4444' }}>-18.4K</span>
              </div>
            </div>
          </div>

          {/* Risk Metrics */}
          <div style={{ backgroundColor: '#1e293b', padding: '16px', borderRadius: '8px', border: '1px solid #334155' }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: '14px', fontWeight: '600', color: '#94a3b8' }}>RISK METRICS</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ fontSize: '12px', color: '#94a3b8' }}>VaR (1D)</span>
                <span style={{ fontSize: '12px', fontWeight: '600', color: '#f59e0b' }}>45K</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ fontSize: '12px', color: '#94a3b8' }}>Max Drawdown</span>
                <span style={{ fontSize: '12px', fontWeight: '600', color: '#ef4444' }}>-2.3%</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ fontSize: '12px', color: '#94a3b8' }}>Sharpe Ratio</span>
                <span style={{ fontSize: '12px', fontWeight: '600', color: '#10b981' }}>1.85</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ fontSize: '12px', color: '#94a3b8' }}>Beta</span>
                <span style={{ fontSize: '12px', fontWeight: '600' }}>0.92</span>
              </div>
            </div>
          </div>

          {/* Market Hours */}
          <div style={{ backgroundColor: '#1e293b', padding: '16px', borderRadius: '8px', border: '1px solid #334155' }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: '14px', fontWeight: '600', color: '#94a3b8' }}>MARKET HOURS</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '12px', color: '#94a3b8' }}>DSE</span>
                <span style={{
                  fontSize: '11px',
                  fontWeight: '600',
                  color: market.open ? '#10b981' : '#ef4444',
                  backgroundColor: market.open ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                  padding: '2px 6px',
                  borderRadius: '4px'
                }}>
                  {market.label}
                </span>
              </div>
              <div style={{ fontSize: '11px', color: '#94a3b8' }}>
                09:30 - 16:00 EAT
              </div>
              <div style={{ fontSize: '11px', color: '#94a3b8' }}>
                {market.window}
              </div>
            </div>
          </div>
        </div>
        
        {/* Center Panel */}
        <div className="center-panel" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {renderPage()}
        </div>

        {/* Right Panel */}
        <div className="right-panel" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <OrderBookPanel />
          <QuickOrderPanel />
          <RecentTradesList />
          <SystemStatusPanel />
        </div>
      </div>
    </div>
  );
}

function App() {
  return (
    <MarketProvider>
      <AppContent />
    </MarketProvider>
  );
}

export default App;
